import styled from 'styled-components';
import { Cable, CheckCircle } from 'lucide-react';

interface ATSConnectionBadgeProps {
  providerNames: string[];
  className?: string;
}

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  background: linear-gradient(to right, #dcfce7, #d1fae5);
  border: 1px solid #86efac;
  font-size: 0.875rem;
  font-weight: 500;
  color: #15803d;
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  color: #15803d;
`;

const CompactBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background: #dcfce7;
  border: 1px solid #86efac;
`;

const CompactCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #15803d;
`;

/**
 * ATS Connection Status Badge
 * Displays connected ATS providers in a compact badge format
 */
export function ATSConnectionBadge({
  providerNames,
  className = '',
}: ATSConnectionBadgeProps) {
  if (providerNames.length === 0) {
    return null;
  }

  // Format provider names for display
  const displayText =
    providerNames.length === 1
      ? `${providerNames[0]} Connected`
      : providerNames.length === 2
      ? `${providerNames.join(' & ')} Connected`
      : `${providerNames.length} ATS Connected`;

  return (
    <BadgeContainer className={className}>
      <IconWrapper>
        <Cable size={16} />
      </IconWrapper>
      <span>{displayText}</span>
      <IconWrapper>
        <CheckCircle size={16} />
      </IconWrapper>
    </BadgeContainer>
  );
}

/**
 * Compact ATS Connection Badge (icon only with tooltip)
 * For use in headers or compact spaces
 */
export function CompactATSConnectionBadge({
  providerNames,
  className = '',
}: ATSConnectionBadgeProps) {
  if (providerNames.length === 0) {
    return null;
  }

  const title =
    providerNames.length === 1
      ? `Connected to ${providerNames[0]}`
      : `Connected to ${providerNames.join(', ')}`;

  return (
    <CompactBadgeContainer className={className} title={title}>
      <IconWrapper>
        <Cable size={14} />
      </IconWrapper>
      <CompactCount>{providerNames.length}</CompactCount>
    </CompactBadgeContainer>
  );
}
